import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Calling from "../../api/Calling";
import Loader from "../../components/Loader";
import { RiCloseFill, RiFileCopyLine, RiCheckLine } from "react-icons/ri";
import { HiOutlineDocumentText } from "react-icons/hi";
import { LuMousePointer2 } from "react-icons/lu";
import { IoPieChart } from "react-icons/io5";
import { MdEdit, MdDeleteSweep, MdPreview } from "react-icons/md";
import { FaExternalLinkAlt, FaCode, FaEye } from "react-icons/fa";

const AddAds = () => {
  const [loader, setLoader] = useState(false);
  const [adsList, setAdsList] = useState([]);
  const profileData = useSelector((state) => state?.profile);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [search, setSearch] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const limit = 100; // Fixed limit to 100
  const [formInfo, setFormInfo] = useState({
    title: "",
    description: "",
    buttonLabel: "",
    buttonUrl: "",
    notes: "",
    type: "banner", // Default type
    display_duration: 30, // Default duration in seconds
  });

  const VALID_TYPES = [
    "interstitial",
    "native",
    "banner",
    "video",
    "onlyimage",
  ]; // example set

  const [openMoreOptions, setOpenMoreOptions] = useState(null);
  const [files, setFiles] = useState({
    icon: null,
    image: null,
    video: null, // Added for video type
  });

  // Add state for existing media URLs when editing
  const [existingMedia, setExistingMedia] = useState({
    icon: "",
    image: "",
    video: "", // Added for video type
  });

  const [fileErrors, setFileErrors] = useState({
    icon: "",
    image: "",
    video: "", // Added for video type
  });

  // Allowed file types
  const allowedTypes = {
    image: ["image/png", "image/jpg", "image/jpeg"],
    icon: ["image/png", "image/jpg", "image/jpeg"],
    video: ["video/mp4"], // Common video formats
  };

  // Max file size
  const MAX_FILE_SIZE = {
    image: 1 * 1024 * 1024, // 1MB in bytes for images
    icon: 1 * 1024 * 1024, // 1MB in bytes for icons
    video: 10 * 1024 * 1024, // 10MB in bytes for videos
  };

  const validateFile = (file, type) => {
    if (!file) return "Please select a file";

    // Check file type
    if (!allowedTypes[type].includes(file.type)) {
      return `Only ${allowedTypes[type].join(", ")} files are allowed`;
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE[type]) {
      return `File size must be less than ${
        MAX_FILE_SIZE[type] / (1024 * 1024)
      }MB`;
    }

    return "";
  };

  const handleSelectAll = () => {
    if (!selectAll) {
      const allUserIds = adsList.map((user) => user._id);
      setSelectedUsers(allUserIds);
    } else {
      setSelectedUsers([]);
    }
    setSelectAll(!selectAll);
  };

  const handleSelectUser = (id) => {
    if (selectedUsers.includes(id)) {
      setSelectedUsers(selectedUsers.filter((userId) => userId !== id));
    } else {
      setSelectedUsers([...selectedUsers, id]);
    }
  };

  // Fixed delete function that properly handles both single and multiple deletions
  const handleDelete = async (id = null) => {
    const content = `You are about to delete a advertisements data. To confirm, please solve this math problem.`;
    const userAnswer = prompt(`${content} Enter 'admin' to confirm:`);

    if (!userAnswer || userAnswer.trim().toLowerCase() !== "admin") {
      toast.error("Deletion not confirmed. Please type 'admin' to proceed.");
      return;
    }

    setLoader(true);

    try {
      // Determine which IDs to delete
      let idsToDelete = [];

      if (id) {
        idsToDelete = [id];
      } else if (selectedUsers.length > 0) {
        idsToDelete = selectedUsers;
      } else {
        toast.error("No advertisements selected for deletion.");
        return;
      }

      // Call delete API
      const response = await Calling.deleteAds({ id: idsToDelete });

      if (response?.status) {
        toast.success(
          response?.message || "Advertisement(s) deleted successfully."
        );
        setSelectedUsers([]);
        setSelectAll(false);
        fetchData(); // Refresh data
      } else {
        toast.error(response?.message || "Failed to delete advertisement(s).");
      }
    } catch (error) {
      console.error("Error deleting advertisements:", error);
      toast.error("An error occurred while deleting advertisement(s).");
    } finally {
      setLoader(false);
    }
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];

    if (file) {
      const error = validateFile(file, type);
      setFileErrors((prev) => ({ ...prev, [type]: error }));

      if (!error) {
        setFiles((prev) => ({ ...prev, [type]: file }));

        // Clear the existing media preview when a new file is selected
        setExistingMedia((prev) => ({ ...prev, [type]: "" }));
      } else {
        e.target.value = ""; // Reset input
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormInfo((prev) => ({ ...prev, [name]: value }));
  };

  // Modified to update the form fields based on ad type
  const handleAdTypeChange = (e) => {
    const newType = e.target.value;
    setFormInfo((prev) => ({ ...prev, type: newType }));

    // Reset media files that aren't relevant to this type
    if (newType === "video") {
      setFileErrors((prev) => ({ ...prev, image: "" }));
    } else {
      setFileErrors((prev) => ({ ...prev, video: "" }));
    }
  };

  const fetchData = async () => {
    setLoader(true);
    try {
      const resp = await Calling.getAds(
        page,
        limit,
        search,
        null,
        profileData?._id
      );
      if (resp?.status) {
        setAdsList(resp.data || []);
        // Updated to match the API response structure
        setTotalPages(resp.totalPage || 0);
      }
    } catch (error) {
      console.error("Failed to fetch ads:", error);
      toast.error("Failed to load ads");
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page]);

  // New function to handle search button click
  const handleSearchClick = () => {
    setPage(0); // Reset to first page when search changes
    fetchData();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formInfo.title || !formInfo.buttonLabel || !formInfo.buttonUrl) {
      return toast.error("Title, Button Label, and Button URL are required");
    }

    // For update, files are optional
    if (!isEditing) {
      // Validate files based on ad type
      if (!files.icon || fileErrors.icon) {
        return toast.error("Please select a valid icon file");
      }

      if (formInfo.type === "video") {
        if (!files.video) {
          return toast.error(
            "Please provide a video file or URL for video ads"
          );
        }
      } else {
        if (!files.image || fileErrors.image) {
          return toast.error("Please select a valid image file");
        }
      }
    }

    setLoader(true);
    try {
      const formData = new FormData();
      formData.append("profile", profileData?._id);
      formData.append("title", formInfo.title);
      formData.append("buttonLabel", formInfo.buttonLabel);
      formData.append("buttonUrl", formInfo.buttonUrl);
      formData.append("type", formInfo.type);
      formData.append("display_duration", formInfo.display_duration);

      // Optional fields
      if (formInfo.description)
        formData.append("description", formInfo.description);
      if (formInfo.notes) formData.append("notes", formInfo.notes);

      // Files - only append if they exist (for update, they might not be changed)
      if (files.icon) formData.append("icon", files.icon);

      // Attach appropriate media based on type
      if (formInfo.type === "video") {
        if (files.video) formData.append("video", files.video);
      } else {
        if (files.image) formData.append("image", files.image);
      }

      let resp;

      if (isEditing && editingId) {
        // Update existing ad
        formData.append("id", editingId);
        resp = await Calling.updateAds(formData);
      } else {
        // Create new ad
        resp = await Calling.addAds(formData);
      }

      if (resp?.status) {
        toast.success(
          resp?.message ||
            `Ad ${isEditing ? "updated" : "uploaded"} successfully!`
        );
        resetForm();
        setPage(0); // Reset to first page after adding/updating ad
        fetchData();
      } else {
        toast.error(
          resp?.message || `Failed to ${isEditing ? "update" : "upload"} ad!`
        );
      }
    } catch (error) {
      console.error(`Error ${isEditing ? "updating" : "uploading"} ad:`, error);
      toast.error(
        `An error occurred while ${isEditing ? "updating" : "uploading"} the ad`
      );
    } finally {
      setLoader(false);
    }
  };

  const resetForm = () => {
    setFormInfo({
      title: "",
      description: "",
      buttonLabel: "",
      buttonUrl: "",
      notes: "",
      type: "banner", // Reset to default type
      display_duration: 30, // Reset to default duration
    });
    setFiles({
      icon: null,
      image: null,
      video: null,
    });
    setFileErrors({
      icon: "",
      image: "",
      video: "",
    });
    setIsEditing(false);
    setEditingId(null);
    // Clear existing media previews
    setExistingMedia({
      icon: "",
      image: "",
      video: "",
    });

    // Reset file inputs
    const fileInputs = document.querySelectorAll('input[type="file"]');
    fileInputs.forEach((input) => {
      input.value = "";
    });
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  // Function to handle edit button click
  const handleEditClick = (ad) => {
    // Fill the form with the ad data
    setFormInfo({
      title: ad.title || "",
      description: ad.description || "",
      buttonLabel: ad.buttonLabel || "",
      buttonUrl: ad.buttonUrl || "",
      notes: ad.notes || "",
      type: ad.type || "banner",
      display_duration: ad.display_duration || 30,
    });

    // Set editing state
    setIsEditing(true);
    setEditingId(ad._id);

    // Store existing media URLs
    setExistingMedia({
      icon: ad.icon || "",
      image: ad.image || "",
      video: ad.video || "", // Add video URL
    });

    // Reset new file states but don't require new files for update
    setFiles({
      icon: null,
      image: null,
      video: null,
    });
    setFileErrors({
      icon: "",
      image: "",
      video: "",
    });

    // Scroll to the form
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < totalPages) {
      setPage(newPage);
    }
  };

  // Generate pagination array
  const getPaginationArray = () => {
    const paginationArray = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow) {
      // If total pages are less than or equal to maxPagesToShow, show all pages
      for (let i = 0; i < totalPages; i++) {
        paginationArray.push(i);
      }
    } else {
      // Always include first page
      paginationArray.push(0);

      // Calculate start and end of the middle pages
      let startPage = Math.max(1, page - 1);
      let endPage = Math.min(startPage + 2, totalPages - 2);

      // Adjust start if end is too small
      if (endPage - startPage < 2) {
        startPage = Math.max(1, endPage - 2);
      }

      // Add ellipsis after first page if needed
      if (startPage > 1) {
        paginationArray.push(-1); // -1 represents ellipsis
      }

      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        paginationArray.push(i);
      }

      // Add ellipsis before last page if needed
      if (endPage < totalPages - 2) {
        paginationArray.push(-2); // -2 represents ellipsis
      }

      // Always include last page
      paginationArray.push(totalPages - 1);
    }

    return paginationArray;
  };

  const handleViewAnalytics = (ad) => {
    window.location.href = `/${ad._id}`;
  };
  const handleViewDetails = (ad) => {
    window.location.href = `/Ads/view/${ad._id}`;
  };
  // Function to toggle the dropdown menu
  const toggleMoreOptions = (adId) => {
    if (openMoreOptions === adId) {
      setOpenMoreOptions(null);
    } else {
      setOpenMoreOptions(adId);
    }
  };

  return (
    <>
      {loader ? (
        <Loader loader={loader} />
      ) : (
        <div className="add-ads-container bg-main-th p-4 mt-2 md:p-6 w-full">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-2xl md:text-3xl font-bold mb-6 text-gray-800 flex items-center">
              <span className="bg-blue-600 text-white p-2 rounded-md mr-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"
                  />
                </svg>
              </span>
              Advertisement Manager
            </h1>
            <form
              onSubmit={handleSubmit}
              className="bg-white p-6 rounded-xl shadow-lg mb-8 border border-gray-100"
            >
              <h2 className="text-xl font-semibold mb-5 text-gray-800 pb-3 border-b">
                {isEditing
                  ? "Update Advertisement"
                  : "Create New Advertisement"}
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Title */}
                <div className="form-group">
                  <label className="block text-gray-700 font-medium mb-2">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formInfo.title}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    required
                    placeholder="Enter ad title"
                  />
                </div>

                {/* Button Label */}
                <div className="form-group">
                  <label className="block text-gray-700 font-medium mb-2">
                    Button Label <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="buttonLabel"
                    value={formInfo.buttonLabel}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    required
                    placeholder="e.g. Learn More, Buy Now, Install"
                  />
                </div>

                {/* Ad Type */}
                <div className="form-group">
                  <label className="block text-gray-700 font-medium mb-2">
                    Ad Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="type"
                    value={formInfo.type}
                    onChange={handleAdTypeChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    required
                  >
                    {VALID_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Display Duration */}
                <div className="form-group">
                  <label className="block text-gray-700 font-medium mb-2">
                    Display Duration (seconds){" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="display_duration"
                    value={formInfo.display_duration}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    required
                    min="1"
                    max="300"
                    placeholder="Duration in seconds"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    How long the ad should be displayed (1-300 seconds)
                  </p>
                </div>

                {/* Button URL */}
                <div className="form-group col-span-1 md:col-span-2">
                  <label className="block text-gray-700 font-medium mb-2">
                    Button URL <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="url"
                    name="buttonUrl"
                    value={formInfo.buttonUrl}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    placeholder="https://example.com"
                    required
                  />
                </div>

                {/* Description - Optional */}
                <div className="form-group col-span-1 md:col-span-2">
                  <label className="block text-gray-700 font-medium mb-2">
                    Description (Optional)
                  </label>
                  <textarea
                    name="description"
                    value={formInfo.description}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    rows="3"
                    placeholder="Write a brief description for your ad"
                  ></textarea>
                </div>

                {/* Notes - Optional */}
                <div className="form-group col-span-1 md:col-span-2">
                  <label className="block text-gray-700 font-medium mb-2">
                    Notes (Optional)
                  </label>
                  <textarea
                    name="notes"
                    value={formInfo.notes}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    rows="2"
                    placeholder="Add any internal notes here"
                  ></textarea>
                </div>

                {/* File Uploads Section */}
                <div className="col-span-1 md:col-span-2 mt-4 mb-2">
                  <h3 className="text-lg font-medium text-gray-800">
                    Media Files
                  </h3>
                  <p className="text-sm text-gray-500">
                    Upload media for your advertisement{" "}
                    {isEditing &&
                      "- Only required for new ads, optional when updating"}
                  </p>
                </div>

                {/* Icon Upload */}
                <div className="form-group">
                  <label className="block text-gray-700 font-medium mb-2">
                    Icon {!isEditing && <span className="text-red-500">*</span>}
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-all">
                    <input
                      type="file"
                      accept=".png,.jpg,.jpeg"
                      onChange={(e) => handleFileChange(e, "icon")}
                      className="w-full text-sm cursor-pointer file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      required={!isEditing}
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      Recommended size: 64x64px (PNG, JPG, JPEG, max 1MB)
                    </p>

                    {/* Icon Preview */}
                    {isEditing && existingMedia.icon && !files.icon && (
                      <div className="mt-3">
                        <p className="text-sm text-gray-600 mb-1">
                          Current Icon:
                        </p>
                        <div className="flex justify-center">
                          <img
                            src={existingMedia.icon}
                            alt="Current Icon"
                            className="w-16 h-16 object-cover rounded-md border border-gray-200"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  {fileErrors.icon && (
                    <p className="text-red-500 text-sm mt-1">
                      {fileErrors.icon}
                    </p>
                  )}
                </div>

                {/* Media Upload - Conditional based on ad type */}
                <div className="form-group">
                  <label className="block text-gray-700 font-medium mb-2">
                    {formInfo.type === "video" ? "Video" : "Image"}{" "}
                    {!isEditing && <span className="text-red-500">*</span>}
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-all">
                    <input
                      type="file"
                      accept={
                        formInfo.type === "video"
                          ? ".mp4"
                          : ".png,.jpg,.jpeg"
                      }
                      onChange={(e) =>
                        handleFileChange(
                          e,
                          formInfo.type === "video" ? "video" : "image"
                        )
                      }
                      className="w-full text-sm cursor-pointer file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      required={!isEditing && formInfo.type !== "video"}
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      {formInfo.type === "video"
                        ? "Supported formats: MP4(max 10MB)"
                        : "Supported formats: (PNG, JPG, JPEG, max 1MB)"}
                    </p>

                    {/* Media Preview */}
                    {isEditing &&
                      ((formInfo.type === "video" &&
                        existingMedia.video &&
                        !files.video) ||
                        (formInfo.type !== "video" &&
                          existingMedia.image &&
                          !files.image)) && (
                        <div className="mt-3">
                          <p className="text-sm text-gray-600 mb-1">
                            Current{" "}
                            {formInfo.type === "video"
                              ? "Video"
                              : "Banner Image"}
                            :
                          </p>
                          <div className="flex justify-center">
                            {formInfo.type === "video" ? (
                              <video
                                src={existingMedia.video}
                                controls
                                className="max-w-full h-auto max-h-40 object-contain rounded-md border border-gray-200"
                              />
                            ) : (
                              <img
                                src={existingMedia.image}
                                alt="Current Banner"
                                className="max-w-full h-auto max-h-40 object-contain rounded-md border border-gray-200"
                              />
                            )}
                          </div>
                        </div>
                      )}
                  </div>
                  {formInfo.type === "video"
                    ? fileErrors.video && (
                        <p className="text-red-500 text-sm mt-1">
                          {fileErrors.video}
                        </p>
                      )
                    : fileErrors.image && (
                        <p className="text-red-500 text-sm mt-1">
                          {fileErrors.image}
                        </p>
                      )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-4 mt-8">
                <button
                  type="submit"
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all shadow-md flex items-center"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-2"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {isEditing ? "Update Advertisement" : "Upload Advertisement"}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-all flex items-center"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-2"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {isEditing ? "Cancel Edit" : "Reset Form"}
                </button>
              </div>
            </form>
            {/* Your Advertisements Section */}
            <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 mr-2 text-blue-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                    />
                  </svg>
                  Your Advertisements
                </h2>
                <div className="w-full md:w-auto flex flex-col md:flex-row gap-2">
                  <div className="relative flex-1">
                    <input
                      type="text"
                      placeholder="Search ads..."
                      value={search}
                      onChange={handleSearchChange}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <div className="absolute left-3 top-2.5 text-gray-400">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                    </div>
                  </div>
                  <button
                    onClick={handleSearchClick}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all flex items-center justify-center"
                  >
                    Search
                  </button>
                </div>
              </div>

              {/* Selection and bulk delete section */}
              <div className="flex items-center mb-4">
                <input
                  type="checkbox"
                  className="mr-2 h-4 w-4"
                  checked={selectAll}
                  onChange={handleSelectAll}
                  id="select-all"
                />
                <label htmlFor="select-all" className="text-sm text-gray-700">
                  Select All
                </label>

                {selectedUsers.length > 0 && (
                  <button
                    onClick={() => handleDelete()}
                    className="ml-4 px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all flex items-center text-sm"
                  >
                    <MdDeleteSweep size={18} className="mr-1" />
                    Delete Selected ({selectedUsers.length})
                  </button>
                )}
              </div>
              {adsList.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {adsList.map((ad) => (
                    <div
                      key={ad?._id}
                      className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all border border-gray-200"
                    >
                      <div className="p-3 border-b border-gray-100 flex items-center">
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(ad._id)}
                          onChange={() => handleSelectUser(ad._id)}
                          className="mr-3 h-4 w-4"
                        />
                        <h3 className="font-bold text-gray-800 flex-1 truncate">
                          {ad.title}
                        </h3>
                        {/* More button with dropdown */}
                        <div className="relative">
                          <button
                            onClick={() => toggleMoreOptions(ad._id)}
                            className="text-gray-600 hover:text-blue-600 p-1 rounded-full hover:bg-gray-100 transition-all"
                            title="More options"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-5 w-5"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                            </svg>
                          </button>

                          {/* Dropdown menu */}
                          {openMoreOptions === ad._id && (
                            <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-xl z-50 border border-gray-200 overflow-hidden">
                              <div className="py-2 divide-y divide-gray-100">
                                <div className="space-y-1">
                                  <button
                                    onClick={() => handleEditClick(ad)}
                                    className="flex items-center w-full px-4 py-2 text-sm font-medium text-gray-700 hover:bg-indigo-50 hover:text-indigo-600 transition-all duration-150"
                                  >
                                    <MdEdit
                                      size={18}
                                      className="mr-3 text-indigo-500 group-hover:text-indigo-600"
                                    />
                                    Edit Advertisement
                                  </button>
                                  <button
                                    onClick={() => handleViewAnalytics(ad)}
                                    className="flex items-center w-full px-4 py-2 text-sm font-medium text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 transition-all duration-150"
                                  >
                                    <IoPieChart
                                      size={18}
                                      className="mr-3 text-emerald-500 group-hover:text-emerald-600"
                                    />
                                    View Analytics
                                  </button>
                                  <button
                                    onClick={() => handleViewDetails(ad)}
                                    className="flex items-center w-full px-4 py-2 text-sm font-medium text-gray-700 hover:text-main-blue hover:text-main-blue/80 transition-all duration-150"
                                  >
                                    <MdPreview
                                      size={20}
                                      className="mr-3 text-main-blue group-hover:text-main-blue/80"
                                    />
                                    View details
                                  </button>
                                  <div className="px-4 py-2">
                                    <ApiDocButton ad={ad} />
                                  </div>
                                </div>
                                <div className="pt-2">
                                  <button
                                    onClick={() => handleDelete(ad._id)}
                                    className="flex items-center w-full px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 hover:text-red-700 transition-all duration-150"
                                  >
                                    <MdDeleteSweep
                                      size={18}
                                      className="mr-3 text-red-500"
                                    />
                                    Delete Advertisement
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      {ad.video ? (
                        <div className="relative group">
                          <video
                            src={ad.video}
                            className="w-full h-48 object-cover transition-all group-hover:brightness-95"
                            muted
                            playsInline
                          />
                          <div className="absolute top-3 left-3 bg-white p-1 rounded-full shadow-md flex items-center justify-center">
                            {ad.icon && (
                              <img
                                src={ad.icon}
                                alt="Icon"
                                className="w-8 h-8 object-cover rounded-full"
                              />
                            )}
                          </div>
                          {/* Install button positioned on the video */}
                          <div className="absolute bottom-3 right-3 z-40">
                            <a
                              href={ad.buttonUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg flex items-center justify-center transition-colors"
                            >
                              <FaExternalLinkAlt size={16} className="mr-2" />
                              {ad.buttonLabel || "Install"}
                            </a>
                          </div>
                          {/* Gradient overlay */}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-40">
                            {/* Overlay for better text visibility */}
                          </div>
                        </div>
                      ) : (
                        ad.image && (
                          <div className="relative group">
                            <img
                              src={ad.image}
                              alt="Ad Banner"
                              className="w-full h-48 object-cover transition-all group-hover:brightness-95"
                            />
                            <div className="absolute top-3 left-3 bg-white p-1 rounded-full shadow-md flex items-center justify-center">
                              {ad.icon && (
                                <img
                                  src={ad.icon}
                                  alt="Icon"
                                  className="w-8 h-8 object-cover rounded-full"
                                />
                              )}
                            </div>
                            {/* Install button positioned on the image */}
                            <div className="absolute bottom-3 right-3 z-40">
                              <a
                                href={ad.buttonUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg flex items-center justify-center transition-colors"
                              >
                                <FaExternalLinkAlt size={16} className="mr-2" />
                                {ad.buttonLabel || "Install"}
                              </a>
                            </div>
                            {/* Gradient overlay */}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-40">
                              {/* Overlay for better text visibility */}
                            </div>
                          </div>
                        )
                      )}

                      <div className="p-5">
                        <div className="flex justify-between items-start mb-3">
                          <h3 className="font-bold text-lg text-gray-800 transition-colors">
                            {ad.title}
                          </h3>
                          <span className="text-sm bg-green-200 text-green-800 px-2 py-1 rounded-full font-medium">
                            {ad.type}
                          </span>
                        </div>

                        {ad.description && (
                          <p className="text-gray-600 mb-4 text-sm line-clamp-2">
                            {ad.description}
                          </p>
                        )}

                        <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                          <div className="flex space-x-4">
                            <div className="flex items-center text-xs text-gray-600">
                              <FaEye size={16} className="mr-1 text-blue-500" />
                              <span>{ad.views.toLocaleString()}</span>
                            </div>
                            <div className="flex items-center text-xs text-gray-600">
                              <LuMousePointer2
                                size={16}
                                className="mr-1 text-green-500"
                              />
                              <span>{ad.clicks.toLocaleString()}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <div className="inline-block p-4 rounded-full bg-blue-100 mb-4">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-10 w-10 text-blue-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">
                    No Advertisements Found
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {search
                      ? "No ads match your search criteria."
                      : "You haven't created any advertisements yet."}
                  </p>
                  {search && (
                    <button
                      onClick={() => setSearch("")}
                      className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-all"
                    >
                      Clear Search
                    </button>
                  )}
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center mt-8">
                  <nav className="flex items-center space-x-2">
                    <button
                      onClick={() => handlePageChange(page - 1)}
                      disabled={page === 0}
                      className={`w-10 h-10 flex items-center justify-center rounded-md ${
                        page === 0
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                      }`}
                      aria-label="Previous page"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 19l-7-7 7-7"
                        />
                      </svg>
                    </button>

                    {getPaginationArray().map((pageNum, index) =>
                      pageNum >= 0 ? (
                        <button
                          key={index}
                          onClick={() => handlePageChange(pageNum)}
                          className={`w-10 h-10 flex items-center justify-center rounded-md ${
                            pageNum === page
                              ? "bg-blue-600 text-white font-medium"
                              : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          {pageNum + 1}
                        </button>
                      ) : (
                        <span
                          key={index}
                          className="text-gray-500 w-10 h-10 flex items-center justify-center"
                        >
                          &hellip;
                        </span>
                      )
                    )}

                    <button
                      onClick={() => handlePageChange(page + 1)}
                      disabled={page === totalPages - 1 || totalPages === 0}
                      className={`w-10 h-10 flex items-center justify-center rounded-md ${
                        page === totalPages - 1 || totalPages === 0
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                      }`}
                      aria-label="Next page"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </button>
                  </nav>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AddAds;

const ApiDocButton = ({ ad }) => {
  const [showDocBox, setShowDocBox] = useState(false);
  const [copied, setCopied] = useState(false);
  const modalRef = useRef(null);

  const handleShowDocs = () => {
    setShowDocBox(true);
    // Lock scrolling when modal opens
    document.body.style.overflow = "hidden";
  };

  const handleClose = () => {
    setShowDocBox(false);
    setCopied(false);
    // Restore scrolling when modal closes
    document.body.style.overflow = "auto";
  };

  const handleCopyApiUrl = (url) => {
    try {
      // Try using the Clipboard API
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url);
      } else {
        // Fallback method using textarea
        const textArea = document.createElement("textarea");
        textArea.value = url;
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy: ", err);
      alert("Could not copy to clipboard. Please copy manually.");
    }
  };

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        handleClose();
      }
    };

    if (showDocBox) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      // Ensure scrolling is restored when component unmounts
      document.body.style.overflow = "auto";
    };
  }, [showDocBox]);

  // Add keyboard events to close modal with ESC key
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        handleClose();
      }
    };

    if (showDocBox) {
      window.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [showDocBox]);

  return (
    <div className="relative">
      <button
        onClick={handleShowDocs}
        className="flex items-center w-full py-2 text-sm font-medium text-gray-700 hover:bg-cyan-50 hover:text-cyan-600 transition-all duration-150"
        title="API Documentation"
      >
        <FaCode
          size={16}
          className="mr-3 text-cyan-500 group-hover:text-cyan-600"
        />
        API Documentation
      </button>

      {showDocBox && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[9999] p-4 ">
          <div
            ref={modalRef}
            className="bg-white rounded-xl max-w-2xl w-full shadow-2xl overflow-hidden"
            style={{ zIndex: 100 }}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center space-x-2">
                <HiOutlineDocumentText size={20} className="text-blue-600" />
                <h2 className="text-xl font-bold text-gray-800">
                  API Documentation for "{ad.title}"
                </h2>
              </div>
              <button
                onClick={handleClose}
                className="text-gray-500 hover:text-gray-700 transition-colors rounded-full hover:bg-gray-200 p-2"
              >
                <RiCloseFill size={22} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* API Documentation - Always showing */}
              <div className="space-y-4">
                <div className="flex items-start space-x-3 mb-4">
                  <HiOutlineDocumentText
                    size={24}
                    className="text-blue-600 flex-shrink-0 "
                  />
                  <div className="text-sm text-gray-700 space-y-2">
                    <p>
                      Use these API endpoints to integrate this ad into your
                      applications or websites.
                    </p>
                    <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                      <p className="font-medium mb-1">Required Headers:</p>
                      <code className="block bg-yellow-100 p-2 rounded">
                        "x-api-key": "s2x9s5zolbHgrDSlDZIk1azM6LzeYOcY"
                      </code>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-gray-50 p-3 border-b border-gray-200">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center space-x-2">
                          <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full font-medium">
                            GET
                          </span>
                          <span className="text-sm font-medium">
                            Fetch ad details
                          </span>
                        </div>
                        <button
                          onClick={() =>
                            handleCopyApiUrl(
                              `https://adsmanager.linqinnovations.com/api/ads/${ad._id}`
                            )
                          }
                          className="text-blue-600 hover:text-blue-800 p-1 rounded transition-colors"
                        >
                          {copied ? (
                            <RiCheckLine size={18} />
                          ) : (
                            <RiFileCopyLine size={18} />
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="p-3 bg-gray-50">
                      <code className="text-sm text-gray-800 break-all">
                        https://adsmanager.linqinnovations.com/api/ads/
                        {ad._id}
                      </code>
                    </div>
                  </div>

                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-gray-50 p-3 border-b border-gray-200">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center space-x-2">
                          <span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded-full font-medium">
                            PUT
                          </span>
                          <span className="text-sm font-medium">
                            Increase click count
                          </span>
                        </div>
                        <button
                          onClick={() =>
                            handleCopyApiUrl(
                              `https://adsmanager.linqinnovations.com/api/ads/click/${ad._id}`
                            )
                          }
                          className="text-blue-600 hover:text-blue-800 p-1 rounded transition-colors"
                        >
                          {copied ? (
                            <RiCheckLine size={18} />
                          ) : (
                            <RiFileCopyLine size={18} />
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="p-3 bg-gray-50">
                      <code className="text-sm text-gray-800 break-all">
                        https://adsmanager.linqinnovations.com/api/ads/click/
                        {ad._id}
                      </code>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end bg-gray-50 p-4 border-t border-gray-200">
              <button
                onClick={handleClose}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-all font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
